import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Input, Select, SegmentedControl, useToast } from "@astrum/ui";
import type { CustomField } from "@astrum/shared";
import { api, ApiError } from "../../lib/api.js";
import type { Task } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import { filterTasks, paramsFromState, stateFromParams, VIEW_PARAM_KEYS } from "../../lib/work-views.js";
import type { ViewState } from "../../lib/work-views.js";
import { CustomFieldFilters } from "./CustomFieldFilters.js";
import { CalendarView } from "./CalendarView.js";
import { SavedViewsMenu } from "./SavedViewsMenu.js";
import { TableView } from "./TableView.js";
import { TimelineView } from "./TimelineView.js";
import type { TaskPatch } from "./types.js";
import "./task-views.css";

const LIMIT = 500;

interface Props {
  projectId: string;
  /** From the project: false for a read-only viewer. The server still decides. */
  canEdit: boolean;
  canShare: boolean;
}

/**
 * The project's Views tab: its tasks as a table, calendar or timeline, with the
 * same saved views and URL state as Work. Filtering is done here on the loaded
 * tasks (a project rarely holds more than a few hundred), so a saved view's
 * filters apply the same way wherever it is opened.
 */
export function ProjectViews({ projectId, canEdit, canShare }: Props) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<CustomField[]>([]);
  const [people, setPeople] = useState<{ id: string; displayName: string }[]>([]);

  const state = useMemo(() => {
    const parsed = stateFromParams(params, "table");
    return parsed.view === "list" || parsed.view === "board" ? { ...parsed, view: "table" as const } : parsed;
  }, [params]);

  const update = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const onState = useCallback(
    (change: Partial<ViewState>) => update(paramsFromState({ ...state, ...change })),
    [state, update],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ tasks: Task[] }>(`/tasks?projectId=${projectId}&limit=${LIMIT}`)
      .then((res) => {
        if (!cancelled) setTasks(res.tasks);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load tasks.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /* The project's fields (columns and filters) and, for person filters, who can be picked. */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ fields: CustomField[] }>(`/projects/${projectId}/custom-fields`)
      .then((res) => !cancelled && setFields(res.fields))
      .catch(() => !cancelled && setFields([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  useEffect(() => {
    if (!fields.some((f) => f.type === "person")) return;
    let cancelled = false;
    api
      .get<{ people: { id: string; displayName: string }[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [fields]);

  const onCustomValue = useCallback(
    async (task: Task, fieldId: string, value: string | number | null) => {
      try {
        const res = await api.put<{ customValues: Task["customValues"] }>(`/tasks/${task.id}/custom-fields/${fieldId}`, { value });
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, customValues: res.customValues } : t)));
      } catch (error: unknown) {
        toast(error instanceof ApiError ? error.message : "Could not save the value.", { tone: "danger" });
        throw error;
      }
    },
    [toast],
  );

  const onPatch = useCallback(
    (task: Task, patch: TaskPatch) => {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));
      api
        .patch<{ task: Task }>(`/tasks/${task.id}`, { ...patch, version: task.version })
        .then((res) => setTasks((prev) => prev.map((t) => (t.id === task.id ? res.task : t))))
        .catch((error: unknown) => {
          setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
          toast(error instanceof ApiError ? error.message : "Could not update task.", { tone: "danger" });
        });
    },
    [toast],
  );

  const visible = useMemo(
    () => filterTasks(tasks, state.filters, viewer?.id, undefined, fields),
    [tasks, state.filters, viewer?.id, fields],
  );
  const canEditTask = useCallback(() => canEdit, [canEdit]);
  const month = params.get("month") ?? undefined;

  const apply = (next: ViewState, id: string | undefined) => {
    const cleared = Object.fromEntries(VIEW_PARAM_KEYS.map((key) => [key, undefined]));
    update({ ...cleared, ...paramsFromState(next), sv: id });
  };

  return (
    <div className="project-detail__body">
      <div className="tv-toolbar">
        <SegmentedControl
          aria-label="View"
          value={state.view}
          onChange={(value) => update({ view: value, sv: undefined })}
          options={[
            { value: "table", label: "Table" },
            { value: "calendar", label: "Calendar" },
            { value: "timeline", label: "Timeline" },
          ]}
        />
        <Input
          aria-label="Search tasks"
          placeholder="Search"
          value={state.filters.q ?? ""}
          onChange={(e) => onState({ filters: { ...state.filters, q: e.target.value || undefined } })}
        />
        <Select
          aria-label="Status"
          value={state.filters.status ?? ""}
          onChange={(e) => onState({ filters: { ...state.filters, status: e.target.value || undefined } })}
        >
          <option value="">Any status</option>
          <option value="todo">To do</option>
          <option value="in_progress">In progress</option>
          <option value="review">Review</option>
          <option value="done">Done</option>
        </Select>
        <label className="tv-toolbar__field">
          <input
            type="checkbox"
            checked={state.filters.mine === true}
            onChange={(e) => onState({ filters: { ...state.filters, mine: e.target.checked || undefined } })}
          />
          Mine
        </label>
        <CustomFieldFilters
          fields={fields}
          filters={state.filters.custom ?? []}
          people={people}
          onChange={(custom) => onState({ filters: { ...state.filters, custom: custom.length ? custom : undefined } })}
        />
        <SavedViewsMenu
          current={state}
          projectId={projectId}
          canShare={canShare}
          activeId={params.get("sv") ?? undefined}
          onApply={apply}
        />
        <span className="tv-toolbar__count">{visible.length} tasks</span>
      </div>

      {loading ? (
        <p className="tv-empty">Loading tasks…</p>
      ) : visible.length === 0 ? (
        <p className="tv-empty">No tasks match these filters.</p>
      ) : state.view === "calendar" ? (
        <CalendarView
          tasks={visible}
          canEdit={canEditTask}
          onPatch={onPatch}
          month={month}
          onMonthChange={(value) => update({ month: value })}
        />
      ) : state.view === "timeline" ? (
        <TimelineView tasks={visible} canEdit={canEditTask} onPatch={onPatch} state={state} onState={onState} />
      ) : (
        <TableView
          tasks={visible}
          canEdit={canEditTask}
          onPatch={onPatch}
          state={state}
          onState={onState}
          customFields={fields}
          onCustomValue={onCustomValue}
        />
      )}
      {tasks.length >= LIMIT && <p className="tv-note">Showing the first {LIMIT} tasks.</p>}
    </div>
  );
}
